/**
 * Manager for camera creation and lifecycle
 */

import {
  ArcRotateCamera,
  type Camera,
  FreeCamera,
  type Scene,
  UniversalCamera,
  Vector3,
} from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager";
import type { CameraConfig } from "./types";

/**
 * Manages camera creation, switching, and lifecycle
 */
export class CameraManager extends BaseManager {
  private scene: Scene | null = null;
  private cameras = new Map<string, Camera>();
  private activeCamera: Camera | null = null;

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    this.emit("cameraManagerReady", {});
  }

  /**
   * Set the scene for camera management
   * @param scene The scene to manage cameras in
   */
  setScene(scene: Scene): void {
    this.ensureInitialized();
    this.scene = scene;
  }

  /**
   * Create a camera based on configuration
   * @param config Camera configuration
   */
  createCamera(config: CameraConfig): Camera {
    this.ensureInitialized();

    if (!this.scene) {
      throw new Error("Scene not set. Call setScene first.");
    }

    if (this.cameras.has(config.name)) {
      throw new Error(`Camera with name ${config.name} already exists`);
    }

    let camera: Camera;

    switch (config.type) {
      case "arcRotate":
        camera = this.createArcRotateCamera(config);
        break;
      case "universal":
        camera = this.createUniversalCamera(config);
        break;
      case "free":
        camera = this.createFreeCamera(config);
        break;
      default:
        throw new Error(`Unsupported camera type: ${config.type}`);
    }

    // Apply common properties
    if (config.fov !== undefined) {
      camera.fov = config.fov;
    }
    if (config.minZ !== undefined) {
      camera.minZ = config.minZ;
    }
    if (config.maxZ !== undefined) {
      camera.maxZ = config.maxZ;
    }

    // Attach controls if requested
    if (
      config.attachControls !== false &&
      this.scene.getEngine().getRenderingCanvas()
    ) {
      camera.attachControl(config.attachControls);
    }

    // Store camera
    this.cameras.set(config.name, camera);

    // Set as active if it's the first camera
    if (!this.activeCamera) {
      this.setActiveCamera(config.name);
    }

    // Emit event
    this.emit("cameraCreated", {
      camera,
      name: config.name,
      type: config.type,
    });

    return camera;
  }

  /**
   * Create an ArcRotateCamera
   */
  private createArcRotateCamera(config: CameraConfig): ArcRotateCamera {
    if (!this.scene) {
      throw new Error("Scene is required to create camera");
    }

    const alpha = config.alpha ?? 0;
    const beta = config.beta ?? 0;
    const radius = config.radius ?? 10;
    const target = config.target
      ? new Vector3(config.target.x, config.target.y, config.target.z)
      : Vector3.Zero();

    return new ArcRotateCamera(
      config.name,
      alpha,
      beta,
      radius,
      target,
      this.scene,
    );
  }

  /**
   * Create a UniversalCamera
   */
  private createUniversalCamera(config: CameraConfig): UniversalCamera {
    if (!this.scene) {
      throw new Error("Scene is required to create camera");
    }

    const position = config.position
      ? new Vector3(config.position.x, config.position.y, config.position.z)
      : new Vector3(0, 0, -10);

    const camera = new UniversalCamera(config.name, position, this.scene);

    if (config.target) {
      camera.setTarget(
        new Vector3(config.target.x, config.target.y, config.target.z),
      );
    }

    return camera;
  }

  /**
   * Create a FreeCamera
   */
  private createFreeCamera(config: CameraConfig): FreeCamera {
    if (!this.scene) {
      throw new Error("Scene is required to create camera");
    }

    const position = config.position
      ? new Vector3(config.position.x, config.position.y, config.position.z)
      : new Vector3(0, 0, -10);

    const camera = new FreeCamera(config.name, position, this.scene);

    if (config.target) {
      camera.setTarget(
        new Vector3(config.target.x, config.target.y, config.target.z),
      );
    }

    return camera;
  }

  /**
   * Set the active camera
   * @param name Camera name
   */
  setActiveCamera(name: string): void {
    const camera = this.cameras.get(name);
    if (!camera) {
      throw new Error(`Camera ${name} not found`);
    }

    if (this.scene) {
      this.scene.activeCamera = camera;
    }
    this.activeCamera = camera;

    this.emit("activeCameraChanged", {
      camera,
      name,
    });
  }

  /**
   * Get a camera by name
   * @param name Camera name
   */
  getCamera(name: string): Camera | undefined {
    return this.cameras.get(name);
  }

  /**
   * Get the active camera
   */
  getActiveCamera(): Camera | null {
    return this.activeCamera;
  }

  /**
   * Get all cameras
   */
  getAllCameras(): Map<string, Camera> {
    return new Map(this.cameras);
  }

  /**
   * Remove a camera
   * @param name Camera name
   */
  removeCamera(name: string): void {
    const camera = this.cameras.get(name);
    if (!camera) {
      return;
    }

    camera.dispose();
    this.cameras.delete(name);

    // If this was the active camera, clear it
    if (this.activeCamera === camera) {
      this.activeCamera = null;
      if (this.scene) {
        this.scene.activeCamera = null;
      }
    }

    this.emit("cameraRemoved", { name });
  }

  /**
   * Clear all cameras
   */
  clearAll(): void {
    this.cameras.forEach((camera) => {
      camera.dispose();
    });
    this.cameras.clear();
    this.activeCamera = null;
    if (this.scene) {
      this.scene.activeCamera = null;
    }
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this.clearAll();
    this.scene = null;
  }
}
